import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { CalendarDays, Save, Users, AlertTriangle, CheckCircle, Clock, Loader2 } from 'lucide-react';

const STATUS_OPTIONS = [
    { value: 'present', label: 'P', color: 'bg-emerald-500', textColor: 'text-white' },
    { value: 'absent', label: 'A', color: 'bg-red-500', textColor: 'text-white' },
    { value: 'half_day', label: 'H', color: 'bg-amber-500', textColor: 'text-white' },
    { value: 'wfh', label: 'W', color: 'bg-blue-500', textColor: 'text-white' },
    { value: 'late', label: 'L', color: 'bg-orange-500', textColor: 'text-white' },
    { value: 'annual_leave', label: 'AL', color: 'bg-teal-500', textColor: 'text-white' },
    { value: 'sick_leave', label: 'SL', color: 'bg-pink-500', textColor: 'text-white' },
];

const STATUS_MAP = {
    present: { label: 'P', color: 'bg-emerald-500 text-white' },
    absent: { label: 'A', color: 'bg-red-500 text-white' },
    half_day: { label: 'H', color: 'bg-amber-500 text-white' },
    wfh: { label: 'W', color: 'bg-blue-500 text-white' },
    late: { label: 'L', color: 'bg-orange-500 text-white' },
    warning: { label: 'W!', color: 'bg-yellow-500 text-black' },
    wfh_unverified: { label: 'W?', color: 'bg-blue-500/40 text-white' },
    wfh_verified: { label: 'W\u2713', color: 'bg-blue-600 text-white' },
    pre_join: { label: '\u00b7', color: 'bg-slate-200/40 text-muted-foreground/50' },
    post_exit: { label: '\u00d7', color: 'bg-slate-200/40 text-muted-foreground/50' },
    annual_leave: { label: 'AL', color: 'bg-teal-500 text-white' },
    sick_leave: { label: 'SL', color: 'bg-pink-500 text-white' },
    on_leave: { label: 'OL', color: 'bg-teal-500/70 text-white' },
    weekend: { label: '-', color: 'bg-muted/30 text-muted-foreground' },
    holiday: { label: 'H', color: 'bg-purple-500/20 text-purple-500' },
    gap: { label: '?', color: 'bg-red-500/10 text-red-500 border border-red-500/30' },
};

export default function BulkAttendancePage() {
    const { user } = useAuth();  // eslint-disable-line no-unused-vars
    const [month, setMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [changes, setChanges] = useState({});
    const [filterGaps, setFilterGaps] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.get(`/hr/attendance/bulk-status?month=${month}`);
            setData(res.data);
            setChanges({});
        } catch (err) {
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setCellStatus = (empId, date, newStatus) => {
        const key = `${empId}_${date}`;
        if (newStatus === 'gap' || newStatus === '') {
            const newChanges = { ...changes };
            delete newChanges[key];
            setChanges(newChanges);
        } else {
            setChanges({ ...changes, [key]: { employee_id: empId, date, status: newStatus } });
        }
    };

    const handleSave = async () => {
        const entries = Object.values(changes);
        if (entries.length === 0) { toast.info('No changes to save'); return; }
        setSaving(true);
        try {
            const res = await apiClient.post('/hr/attendance/bulk-entry', entries);
            const { created = 0, updated = 0, leaves_recorded = 0 } = res.data || {};
            const parts = [`${created} created`, `${updated} updated`];
            if (leaves_recorded > 0) parts.push(`${leaves_recorded} leave${leaves_recorded === 1 ? '' : 's'} deducted from balance`);
            toast.success(`Saved: ${parts.join(' \u00b7 ')}`);
            if (res.data.errors?.length) {
                res.data.errors.forEach(e => toast.error(e));
            }
            setChanges({});
            fetchData();
        } catch (err) {
            toast.error('Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    const fillGapsAs = (status) => {
        if (!data) return;
        const newChanges = { ...changes };
        data.employees.forEach(emp => {
            emp.days.forEach(day => {
                if (day.is_gap) {
                    const key = `${emp.employee_id}_${day.date}`;
                    if (!newChanges[key]) {
                        newChanges[key] = { employee_id: emp.employee_id, date: day.date, status };
                    }
                }
            });
        });
        setChanges(newChanges);
        toast.success(`Filled ${Object.keys(newChanges).length - Object.keys(changes).length} gaps as "${status}"`);
    };

    const monthOptions = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
        monthOptions.push({ value: val, label });
    }

    const displayEmployees = filterGaps && data ? data.employees.filter(e => e.gap_count > 0) : (data?.employees || []);
    const totalGaps = data?.employees?.reduce((s, e) => s + e.gap_count, 0) || 0;
    const changesCount = Object.keys(changes).length;

    return (
        <div className="space-y-4" data-testid="bulk-attendance-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" /> Bulk Attendance Entry</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Click any cell to pick a status. AL / SL auto-deduct leave balance on save.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={month} onValueChange={setMonth}>
                        <SelectTrigger className="w-44" data-testid="month-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant={filterGaps ? 'default' : 'outline'} size="sm" onClick={() => setFilterGaps(!filterGaps)} data-testid="filter-gaps-btn">
                        <AlertTriangle className="h-4 w-4 mr-1" /> {filterGaps ? `Gaps Only (${totalGaps})` : `Show Gaps`}
                    </Button>
                </div>
            </div>

            {/* Stats + Actions */}
            <div className="flex flex-wrap items-center gap-3">
                <Badge variant="outline" className="text-sm gap-1"><Users className="h-3.5 w-3.5" /> {data?.employees?.length || 0} employees</Badge>
                <Badge variant="outline" className="text-sm gap-1"><Clock className="h-3.5 w-3.5" /> {data?.working_days || 0} working days</Badge>
                {totalGaps > 0 && <Badge variant="destructive" className="text-sm gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {totalGaps} gaps</Badge>}
                {changesCount > 0 && <Badge className="bg-primary text-primary-foreground text-sm gap-1">{changesCount} pending changes</Badge>}
                <div className="ml-auto flex gap-2">
                    {totalGaps > 0 && (
                        <>
                            <Button variant="outline" size="sm" onClick={() => fillGapsAs('present')} data-testid="fill-present-btn">Fill Gaps → Present</Button>
                            <Button variant="outline" size="sm" onClick={() => fillGapsAs('absent')} data-testid="fill-absent-btn">Fill Gaps → Absent</Button>
                        </>
                    )}
                    <Button size="sm" onClick={handleSave} disabled={changesCount === 0 || saving} data-testid="save-bulk-btn" className="min-w-[100px]">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                        Save ({changesCount})
                    </Button>
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(STATUS_MAP).map(([key, val]) => (
                    <span key={key} className={`px-2 py-0.5 rounded ${val.color} font-medium`}>{val.label} = {key}</span>
                ))}
            </div>

            {/* Attendance Grid */}
            {loading ? (
                <Card><CardContent className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="mt-2 text-muted-foreground">Loading attendance data...</p></CardContent></Card>
            ) : data ? (
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="border-b">
                                        <th className="sticky left-0 bg-card z-10 text-left p-2 min-w-[180px] border-r">Employee</th>
                                        <th className="sticky left-[180px] bg-card z-10 text-center p-1 min-w-[40px] border-r">Gaps</th>
                                        {data.dates.map(d => (
                                            <th key={d.date} className={`text-center p-1 min-w-[28px] ${d.is_weekend ? 'bg-muted/30' : d.is_holiday ? 'bg-purple-500/5' : ''}`}>
                                                <div className="font-mono text-[9px] text-muted-foreground">{d.day_name}</div>
                                                <div className="font-mono">{d.date.slice(8)}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayEmployees.map(emp => (
                                        <tr key={emp.employee_id} className="border-b hover:bg-muted/20">
                                            <td className="sticky left-0 bg-card z-10 p-2 border-r">
                                                <div className="font-medium text-xs truncate max-w-[160px]">{emp.employee_name}</div>
                                                <div className="text-[9px] text-muted-foreground">{emp.department}</div>
                                            </td>
                                            <td className="sticky left-[180px] bg-card z-10 text-center p-1 border-r">
                                                {emp.gap_count > 0 ? (
                                                    <span className="text-red-500 font-bold">{emp.gap_count}</span>
                                                ) : (
                                                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mx-auto" />
                                                )}
                                            </td>
                                            {emp.days.map(day => {
                                                const changeKey = `${emp.employee_id}_${day.date}`;
                                                const changed = changes[changeKey];
                                                const status = changed ? changed.status : day.status;
                                                const info = STATUS_MAP[status] || STATUS_MAP.gap;
                                                const isClickable = !['weekend', 'holiday', 'pre_join', 'post_exit'].includes(day.status);
                                                const cellPill = (
                                                    <div className={`w-6 h-6 rounded flex items-center justify-center mx-auto text-[9px] font-bold ${info.color}`}>
                                                        {info.label}
                                                    </div>
                                                );
                                                if (!isClickable) {
                                                    return (
                                                        <td key={day.date} className="text-center p-0.5"
                                                            data-testid={`cell-${emp.employee_id}-${day.date}`}>
                                                            {cellPill}
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td key={day.date}
                                                        className={`text-center p-0.5 ${changed ? 'ring-2 ring-primary ring-inset' : ''}`}
                                                        data-testid={`cell-${emp.employee_id}-${day.date}`}>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <button type="button" className="cursor-pointer">
                                                                    {cellPill}
                                                                </button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-56 p-2" align="center">
                                                                <p className="text-[11px] font-medium text-muted-foreground mb-2">
                                                                    {emp.employee_name} · {day.date.slice(5)}
                                                                </p>
                                                                <div className="grid grid-cols-4 gap-1">
                                                                    {STATUS_OPTIONS.map(opt => (
                                                                        <button
                                                                            key={opt.value}
                                                                            type="button"
                                                                            onClick={() => setCellStatus(emp.employee_id, day.date, opt.value)}
                                                                            className={`h-8 rounded text-[11px] font-bold ${opt.color} ${opt.textColor} hover:opacity-80 transition ${status === opt.value ? 'ring-2 ring-offset-1 ring-primary' : ''}`}
                                                                            data-testid={`status-pick-${opt.value}`}
                                                                            title={opt.value.replace('_', ' ')}
                                                                        >
                                                                            {opt.label}
                                                                        </button>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCellStatus(emp.employee_id, day.date, 'gap')}
                                                                        className="h-8 rounded text-[11px] font-medium bg-muted hover:bg-muted/80 transition col-span-4"
                                                                        data-testid="status-pick-clear"
                                                                    >
                                                                        Clear change
                                                                    </button>
                                                                </div>
                                                                <p className="text-[10px] text-muted-foreground mt-2">
                                                                    AL / SL auto-deducts leave balance on save.
                                                                </p>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
}
